import { CognitoUserPool, CognitoUser, AuthenticationDetails, CognitoUserSession } from 'amazon-cognito-identity-js';
import { debug, formatSafeErrorMessage } from "../utils/debugLogger";

const poolData = {
  UserPoolId: process.env.REACT_APP_USER_POOL_ID || '', // Fallback for local dev
  ClientId: process.env.REACT_APP_CLIENT_ID || '' // Fallback for local dev
};


export const userPool = new CognitoUserPool(poolData);

interface AuthResult {
  username: string;
  idToken: string;
  displayName: string; // Added display name field
}

/**
 * Authentication service for Cognito
 */
export class CognitoAuthService {
  /**
   * Get the current authenticated user session
   * @returns User session information or null if no session
   */
  static getCurrentSession(): Promise<AuthResult | null> {
    const currentUser = userPool.getCurrentUser();
    
    if (!currentUser) {
      return Promise.resolve(null);
    }

    return new Promise((resolve) => {
      currentUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session) {
          debug.error("Session error:", formatSafeErrorMessage(err));
          resolve(null);
          return;
        }
        
        const idToken = session.getIdToken().getJwtToken();
        const payload = this.parseTokenPayload(idToken);
        
        // Get the display name from the token claims
        const displayName = this.extractDisplayName(payload);
        
        resolve({
          username: currentUser.getUsername(),
          idToken: idToken,
          displayName: displayName
        });
      });
    });
  }

  /**
   * Exchange the refresh token for a NEW ID token now, even if the current one
   * is still valid. getSession() alone returns the cached token until it
   * expires, which is not enough after a Brand is created: the token carries the
   * caller's Brands (sagelyn_brands claim, added by customer-management's
   * Pre Token Generation trigger), so the new Brand only appears in a new token.
   */
  static forceRefresh(): Promise<string> {
    const currentUser = userPool.getCurrentUser();
    if (!currentUser) {
      return Promise.reject(new Error('No authenticated user'));
    }
    return new Promise((resolve, reject) => {
      currentUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session) {
          reject(err || new Error('No valid session'));
          return;
        }
        currentUser.refreshSession(session.getRefreshToken(), (refreshErr: any, fresh: CognitoUserSession) => {
          if (refreshErr || !fresh) {
            reject(refreshErr || new Error('Refresh returned no session'));
            return;
          }
          resolve(fresh.getIdToken().getJwtToken());
        });
      });
    });
  }

  /**
   * Login with email and password
   */
  static login(email: string, password: string): Promise<AuthResult> {
    return new Promise((resolve, reject) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: email,
        Password: password,
      });

      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (result) => {
          const idToken = result.getIdToken().getJwtToken();
          const payload = this.parseTokenPayload(idToken);
          
          // Get the display name from the token claims
          const displayName = this.extractDisplayName(payload);
          
          resolve({
            username: email,
            idToken: idToken,
            displayName: displayName
          });
        },
        onFailure: (err) => {
          reject(err);
        },
        newPasswordRequired: (userAttributes, requiredAttributes) => {
          // Store the actual CognitoUser object in a global variable
          (window as any).__tempCognitoUser = cognitoUser;
        // Store the cognitoUser object for later use
        sessionStorage.setItem('tempCognitoUser', JSON.stringify({
          username: email,
          userAttributes: userAttributes
        }));

        // Reject with special error to handle in the UI
        reject({
          code: 'NewPasswordRequired',
          message: 'You need to set a new password',
          userAttributes,
          requiredAttributes
        });
      },
      totpRequired: (challengeName, challengeParameters) => {
        // Same stash-and-resume pattern as newPasswordRequired above — safe to
        // reuse the same slot since one authenticateUser call only ever hits
        // one challenge type.
        (window as any).__tempCognitoUser = cognitoUser;
        sessionStorage.setItem('tempCognitoUser', JSON.stringify({
          username: email
        }));

        reject({
          code: 'SoftwareTokenMfaRequired',
          message: 'Enter the 6-digit code from your authenticator app',
          challengeName,
          challengeParameters
        });
      }
      });
    });
  }

  /**
   * Sign out the current user
   */
  static logout(): void {
    const currentUser = userPool.getCurrentUser();
    if (currentUser) {
      currentUser.signOut();
    }
  }

  /**
   * Parse a JWT token payload
   */
  static parseTokenPayload(token: string): any {
    try {
      return JSON.parse(atob(token.split(".")[1]));
    } catch (error) {
      debug.error("Error parsing token:", formatSafeErrorMessage(error));
      throw error;
    }
  }

  /**
   * Extract a user-friendly display name from token claims
   */
  static extractDisplayName(payload: any): string {
    // Try different claims in order of preference
    if (payload.name) {
      return payload.name;
    }
    
    // If name not available, try to construct from given_name and family_name
    if (payload.given_name || payload.family_name) {
      const firstName = payload.given_name || '';
      const lastName = payload.family_name || '';
      return `${firstName} ${lastName}`.trim();
    }
    
    // Fall back to preferred_username if available
    if (payload.preferred_username) {
      return payload.preferred_username;
    }
    
    // Fall back to email if available
    if (payload.email) {
      // Optionally remove the domain part for cleaner display
      return payload.email.split('@')[0];
    }
    
    // Last resort: use the username/sub
    return payload['cognito:username'] || payload.sub || 'User';
  }

  /**
   * Check if a token has expired
   */
  static isTokenExpired(token: string): boolean {
    try {
      const payload = this.parseTokenPayload(token);
      const expirationTime = payload.exp * 1000; // Convert to milliseconds
      return Date.now() >= expirationTime;
    } catch (error) {
      return true; // If we can't parse the token, assume it's expired
    }
  }
  /**
 * Complete the new password challenge for users with temporary passwords
 */
static completeNewPasswordChallenge(email: string, newPassword: string): Promise<AuthResult> {
  return new Promise((resolve, reject) => {
    try {
      // Get the CognitoUser object from global variable
      const tempCognitoUser = (window as any).__tempCognitoUser;
      
      // Retrieve stored user data
      const storedData = sessionStorage.getItem('tempCognitoUser');
      if (!storedData) {
        return reject(new Error('No temporary user data found. Please try logging in again.'));
      }
      
      const { username, userAttributes } = JSON.parse(storedData);
      
      // Use stored user or create a new one
      const cognitoUser = tempCognitoUser || new CognitoUser({
        Username: email || username,
        Pool: userPool,
      });
      
      // Define callback handlers
      const callbacks = {
        onSuccess(result: CognitoUserSession) {
          // Clean up temporary storage
          delete (window as any).__tempCognitoUser;
          sessionStorage.removeItem('tempCognitoUser');

          const idToken = result.getIdToken().getJwtToken();
          const payload = CognitoAuthService.parseTokenPayload(idToken);
          const displayName = CognitoAuthService.extractDisplayName(payload);

          resolve({
            username: email || username,
            idToken: idToken,
            displayName: displayName
          });
        },
        
        onFailure(err:Error) {
          reject(err);
        }
      };
      
      // Complete the challenge with our callbacks
      cognitoUser.completeNewPasswordChallenge(newPassword, {}, callbacks);
    } catch (error) {
      reject(error);
    }
  });
}
  /**
 * Complete the SOFTWARE_TOKEN_MFA challenge for a user who has TOTP enrolled
 */
static completeMfaChallenge(email: string, code: string): Promise<AuthResult> {
  return new Promise((resolve, reject) => {
    try {
      const tempCognitoUser = (window as any).__tempCognitoUser;

      const storedData = sessionStorage.getItem('tempCognitoUser');
      if (!storedData) {
        return reject(new Error('No pending MFA challenge found. Please try logging in again.'));
      }

      const { username } = JSON.parse(storedData);

      const cognitoUser = tempCognitoUser || new CognitoUser({
        Username: email || username,
        Pool: userPool,
      });

      cognitoUser.sendMFACode(code, {
        onSuccess(result: CognitoUserSession) {
          delete (window as any).__tempCognitoUser;
          sessionStorage.removeItem('tempCognitoUser');

          const idToken = result.getIdToken().getJwtToken();
          const payload = CognitoAuthService.parseTokenPayload(idToken);
          const displayName = CognitoAuthService.extractDisplayName(payload);

          resolve({
            username: email || username,
            idToken: idToken,
            displayName: displayName
          });
        },
        onFailure(err: Error) {
          reject(err);
        }
      }, 'SOFTWARE_TOKEN_MFA');
    } catch (error) {
      reject(error);
    }
  });
}
  /**
   * Get the current user's CognitoUser with a valid session attached —
   * required before any authenticated Cognito API call (associateSoftwareToken,
   * verifySoftwareToken, setUserMfaPreference, getUserData all need it).
   */
  private static getAuthenticatedCognitoUser(): Promise<CognitoUser> {
    return new Promise((resolve, reject) => {
      const currentUser = userPool.getCurrentUser();
      if (!currentUser) {
        reject(new Error('No authenticated user'));
        return;
      }
      currentUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session) {
          reject(err || new Error('No valid session'));
          return;
        }
        resolve(currentUser);
      });
    });
  }

  /**
   * Begin TOTP enrollment — returns the secret to render as a QR code / manual entry
   */
  static associateSoftwareToken(): Promise<string> {
    return this.getAuthenticatedCognitoUser().then(
      (cognitoUser) =>
        new Promise<string>((resolve, reject) => {
          cognitoUser.associateSoftwareToken({
            associateSecretCode: (secretCode: string) => resolve(secretCode),
            onFailure: (err: any) => reject(err),
          });
        })
    );
  }

  /**
   * Verify the authenticator app's code and enable TOTP as the preferred MFA
   */
  static verifySoftwareToken(code: string, deviceName: string): Promise<void> {
    return this.getAuthenticatedCognitoUser().then(
      (cognitoUser) =>
        new Promise<void>((resolve, reject) => {
          cognitoUser.verifySoftwareToken(code, deviceName, {
            onSuccess: () => {
              cognitoUser.setUserMfaPreference(
                null,
                { PreferredMfa: true, Enabled: true },
                (err) => {
                  if (err) {
                    reject(err);
                    return;
                  }
                  resolve();
                }
              );
            },
            onFailure: (err: Error) => reject(err),
          });
        })
    );
  }

  /**
   * Whether the current user has TOTP MFA enabled today
   */
  static getMfaStatus(): Promise<boolean> {
    return this.getAuthenticatedCognitoUser().then(
      (cognitoUser) =>
        new Promise<boolean>((resolve, reject) => {
          // bypassCache is required — the SDK caches getUserData() in storage
          // and neither setUserMfaPreference nor verifySoftwareToken
          // invalidates it, so a status check right after enroll/disable
          // would otherwise silently return the pre-change value.
          cognitoUser.getUserData((err, data) => {
            if (err) {
              reject(err);
              return;
            }
            resolve((data?.UserMFASettingList || []).includes('SOFTWARE_TOKEN_MFA'));
          }, { bypassCache: true });
        })
    );
  }

  /**
   * Turn TOTP MFA back off
   */
  static disableMfa(): Promise<void> {
    return this.getAuthenticatedCognitoUser().then(
      (cognitoUser) =>
        new Promise<void>((resolve, reject) => {
          cognitoUser.setUserMfaPreference(
            null,
            { PreferredMfa: false, Enabled: false },
            (err) => {
              if (err) {
                reject(err);
                return;
              }
              resolve();
            }
          );
        })
    );
  }
  /**
 * Initiate forgot password flow
 * @param email User's email address
 * @returns Promise that resolves when the reset code is sent
 */
static forgotPassword(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.forgotPassword({
      onSuccess: () => {
        resolve();
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
}
  /**
   * Reset password using the verification code and new password
   * @param email User's email address
   * @param verificationCode Verification code sent to the user's email
   * @param newPassword New password to set
   * @returns Promise that resolves when the password is reset
   */
  static resetPassword(email: string, verificationCode: string, newPassword: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      cognitoUser.confirmPassword(verificationCode, newPassword, {
        onSuccess: () => {
          resolve();
        },
        onFailure: (err) => {
          reject(err);
        },
      });
    });
  }
  /**
   * Extracts the user email from an ID token
   * @param idToken The ID token from Cognito
   * @returns The user's email or empty string if not found or token invalid
   */
  static getUserEmail(idToken: string | null | undefined): string {
    if (!idToken) return "";
    
    try {
      const payload = this.parseTokenPayload(idToken);
      return payload.email || "";
    } catch (error) {
      debug.error("Error extracting email from token:", formatSafeErrorMessage(error));
      return "";
    }
  }

  /**
   * Extracts the user ID from an ID token
   * @param idToken The ID token from Cognito
   * @returns The user's Cognito ID (sub) or empty string if not found or token invalid
   */
  static getUserId(idToken: string | null | undefined): string {
    if (!idToken) return "";
    
    try {
      const payload = this.parseTokenPayload(idToken);
      debug.log("Token parsed for user id", { hasSub: !!payload.sub });
      return payload.sub || "";
    } catch (error) {
      debug.error("Error extracting user ID from token:", formatSafeErrorMessage(error));
      return "";
    }
  }

}

